/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useEffect, useMemo, useState } from "react";
import ButtonLoading from "../../../Button/ButtonLoading";
import LegalbirdIoModal from "../../../Modal/LegalbirdIoModal";
import { AutoAwesomeOutlined } from "@mui/icons-material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import ApiClient from "../../../../services/ApiClient";
import { Box, MenuItem, Select, TextField } from "@mui/material";
import { useFormContext } from "react-hook-form-mui";
import { useCurrentEditor } from "@tiptap/react";
import aiWizardModalStyles from "./aiWizardModalStyles";
import { aiTextFormatter } from "../../../../services/AiWizzard/aiHelperService";
import { useCurrentUser } from "../../../../provider/CurrentUserProvider";
import { ProductUnion } from "../../../../types/ProductUnion";
import { AiDocumentsSelect } from "../../../AiDocumentsSelect/AiDocumentsSelect";
import { AiEventStreamProvider } from "../../../../provider/AiEventStreamProvider";
import { useAiEventStreamControls } from "../../../../hooks/useAiEventStream";
import { PromptGenerationStreamOutputWithRandomEmptyText } from "../../../AiEventStream/PromptGenerationStreamOutputWithRandomEmptyText";

export type AiWizardModalProps = {
  product: ProductUnion;
  componentScopes?: Record<string, AiWizardScope>;
  isRefinement: boolean;
  setIsRefinement: Function;
  generatedText: string;
  setGeneratedText: Function;
  open: boolean;
  handleClose: () => void;
  defaultScopeKey?: string;
  promptContext?: Record<string, any>;
};

type AiWizardScope = {
  name: string;
  scope: string;
  isEnabled: (product: ProductUnion) => boolean;
  disablePrompt?: boolean;
  disableSalutationAndGreeting?: boolean;
};

type AiWizardFormValues = {
  documents: number[];
  recipientType?: string;
  [key: string]: any;
};

export const defaultScopes: Record<string, AiWizardScope> = {
  customExtrajudicialLetter: {
    name: "Außergerichtliches Schreiben",
    scope: "extrajudicialClaimLetter",
    disablePrompt: true,
    isEnabled: (product: ProductUnion) =>
      ["generalCivilLaw", "employmentContract", "civilServiceLaw", "rentalContract", "trafficAccident"].includes(
        product.productClassName
      ) || product.productCategory === "contractLaw",
  },
  statementOfClaim: {
    name: "Klageschrift",
    scope: "complaint",
    disablePrompt: true,
    isEnabled: (product: ProductUnion) =>
      ["generalCivilLaw", "employmentContract", "civilServiceLaw", "rentalContract", "trafficAccident"].includes(
        product.productClassName
      ) || product.productCategory === "contractLaw",
    disableSalutationAndGreeting: true,
  },
  answerToTheComplaint: {
    name: "Replik",
    scope: "reply",
    disablePrompt: true,
    isEnabled: (product: ProductUnion) =>
      ["generalCivilLaw", "employmentContract", "civilServiceLaw", "rentalContract", "trafficAccident"].includes(
        product.productClassName
      ) || product.productCategory === "contractLaw",
    disableSalutationAndGreeting: true,
  },
  preliminaryProceedingsLetter: {
    name: "Schreiben Vorverfahren",
    scope: "preliminaryProceedingsLetter",
    disablePrompt: true,
    isEnabled: (product: ProductUnion) => product.productCategory === "administrativeLaw",
  },
  violationDefenseLetter: {
    name: "Einlassung",
    scope: "violationDefenseLetter",
    disablePrompt: true,
    disableSalutationAndGreeting: true,
    isEnabled: (product: ProductUnion) => product.productClassName === "traffic",
  },
  ceaseAndDesistLetter: {
    name: "Strafbewehrte Unterlassungserklärung",
    scope: "ceaseAndDesistLetter",
    disablePrompt: true,
    disableSalutationAndGreeting: true,
    isEnabled: (product: ProductUnion) => product.productClassName === "generalCivilLaw",
  },
  settlementLetter: {
    name: "Vergleichsvorschlag",
    scope: "settlementLetter",
    disablePrompt: true,
    isEnabled: (product: ProductUnion) =>
      ["settlement", "employmentContract", "rentalContract"].includes(product.productClassName),
  },
  customPdf: {
    name: "Keine Vorlage",
    scope: "customCaseLetter",
    isEnabled: (product: ProductUnion) =>
      [
        "generalCivilLaw",
        "employmentContract",
        "civilServiceLaw",
        "rentalContract",
        "divorce",
        "trafficAccident",
        "settlement",
        "traffic",
      ].includes(product.productClassName) || product.productCategory === "contractLaw",
  },
};

const AiWizardModalContent = ({
  product,
  componentScopes = defaultScopes,
  isRefinement,
  setIsRefinement,
  generatedText,
  setGeneratedText,
  open,
  handleClose,
  defaultScopeKey = "customPdf",
  promptContext = {},
}: AiWizardModalProps) => {
  const { editor } = useCurrentEditor();
  const { resetField, getValues } = useFormContext<AiWizardFormValues>();
  const { connect, reset, completePromptGeneration } = useAiEventStreamControls();

  const [isLoading, setIsLoading] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [type, setType] = useState(defaultScopeKey);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [hasStartedGeneration, setHasStartedGeneration] = useState(false);

  const currentUser = useCurrentUser();

  useEffect(() => {
    setType(defaultScopeKey);
  }, [defaultScopeKey]);

  useEffect(() => {
    resetField("documents", { defaultValue: [] });
  }, [type, resetField]);

  const documentsProduct = useMemo(
    () => ({
      id: product.id,
      type: product.productClassName,
    }),
    [product.id, product.productClassName]
  );

  if (!editor || !product) {
    return null;
  }

  const getSelectedScope = (): AiWizardScope => {
    return componentScopes[type] || componentScopes[defaultScopeKey];
  };

  const getSelectedScopeName = (): string => {
    return getSelectedScope()?.scope || defaultScopeKey;
  };

  const handleWizardClose = () => {
    reset();
    resetField("documents", { defaultValue: [] });
    setIsLoading(false);
    setIsRefinement(false);
    setPrompt("");
    setGeneratedText("");
    setRequestError(null);
    setHasStartedGeneration(false);
    setType(defaultScopeKey);
    handleClose();
  };

  const refineText = async () => {
    const formValues = getValues();

    setIsLoading(true);
    setRequestError(null);
    setHasStartedGeneration(true);
    const streamId = connect();

    try {
      const response = await ApiClient.post(`/lb/services/ai_wizard/refine_text`, {
        body: JSON.stringify({
          promptContext: promptContext,
          productId: product.id,
          originalScope: getSelectedScopeName(),
          originalText: generatedText,
          refinementPrompt: prompt,
          documents: formValues.documents,
          streamId: streamId,
        }),
      });

      const finalText = response["generatedText"] || "";

      setGeneratedText(finalText);
      completePromptGeneration(finalText, undefined, "html");
      setIsRefinement(true);
    } catch (error) {
      reset();
      setRequestError("Der Text konnte nicht optimiert werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const generateText = async () => {
    const formValues = getValues();

    setIsLoading(true);
    setRequestError(null);
    setHasStartedGeneration(true);
    const streamId = connect();

    try {
      const response = await ApiClient.post(`/lb/services/ai_wizard/generate_text`, {
        body: JSON.stringify({
          promptContext: promptContext,
          productId: product.id,
          scope: getSelectedScopeName(),
          additionalContext: prompt,
          documents: formValues.documents,
          streamId: streamId,
        }),
      });

      const finalText = aiTextFormatter(
        product,
        formValues,
        currentUser,
        type,
        response["generatedText"] || "",
        formValues.recipientType || "",
        getSelectedScope()?.disableSalutationAndGreeting
      );

      setGeneratedText(finalText);
      completePromptGeneration(finalText, undefined, "html");
      setIsRefinement(true);
    } catch (error) {
      reset();
      setRequestError("Der Text konnte nicht erstellt werden.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    handleWizardClose();
    editor.commands.clearContent();
    editor.commands.insertContent(generatedText);
    setGeneratedText("");
    setPrompt("");
  };

  const showPromptField = componentScopes[type]?.disablePrompt !== true || isRefinement;

  return (
    <LegalbirdIoModal
      handleClose={handleWizardClose}
      open={open}
      title={"Text erstellen lassen"}
      disableBackdropClick={true}
      maxWidth={"md"}
      fullWidth={true}
      submitButton={
        <ButtonLoading
          type={"submit"}
          variant={"contained"}
          isLoading={isLoading}
          onClick={handleClick}
          disabled={generatedText === "" || isLoading}
        >
          Text übernehmen
        </ButtonLoading>
      }
    >
      <Grid container spacing={3}>
        <Grid
          sx={{ marginTop: "1rem" }}
          size={{
            xs: 6,
            md: 6
          }}>
          <Select value={type} onChange={(e) => setType(e.target.value as string)} disabled={isRefinement} fullWidth>
            {Object.entries(componentScopes).map(([key, scope]) =>
              scope.isEnabled(product) ? (
                <MenuItem key={key} value={key}>
                  {scope.name}
                </MenuItem>
              ) : null
            )}
          </Select>
        </Grid>

        <Grid
          sx={{ marginTop: "1rem" }}
          size={{
            xs: 6,
            md: 6
          }}>
          <AiDocumentsSelect product={documentsProduct} />
        </Grid>

        {showPromptField && (
          <Grid size={12}>
            <TextField
              label={isRefinement ? "Optimierungsinput" : "Prompt"}
              margin={"none"}
              multiline
              rows={6}
              variant="outlined"
              fullWidth
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isLoading}
            />
          </Grid>
        )}

        <Grid size={12}>
          {isRefinement ? (
            <Button
              variant="text"
              color="primary"
              startIcon={<AutoAwesomeOutlined />}
              onClick={refineText}
              disabled={isLoading || (showPromptField && !prompt)}
            >
              Ergebnis optimieren
            </Button>
          ) : (
            <Button
              variant="text"
              color="primary"
              startIcon={<AutoAwesomeOutlined />}
              onClick={generateText}
              disabled={isLoading || (showPromptField && !prompt)}
            >
              Text erstellen
            </Button>
          )}
        </Grid>

        {(hasStartedGeneration || requestError) && (
          <Grid size={12}>
            {requestError && <Box sx={{ color: "error.main", mb: 1 }}>{requestError}</Box>}

            <PromptGenerationStreamOutputWithRandomEmptyText sx={aiWizardModalStyles.htmlOutput} />
          </Grid>
        )}
      </Grid>
    </LegalbirdIoModal>
  );
};

const AiWizardModal = (props: AiWizardModalProps) => {
  return (
    <AiEventStreamProvider>
      <AiWizardModalContent {...props} />
    </AiEventStreamProvider>
  );
};

export default AiWizardModal;
